import { useEffect, useState } from "react";

// ==========================
// CONFIG
// ==========================
const ASSETS = ["BTC-USDT", "XAUT-USDT"] ;
const RSI_PERIOD = 14;
const UPDATE_MS = 60000;
const TIMEFRAME = "4H";
const WEEK_TF = "1W";
const CANDLES = 120;

// ==========================
// COLOR MAP
// ==========================
const COLOR_MAP = {
  emerald: {
    badge: "bg-emerald-600/20 text-emerald-400 border-emerald-500/30",
    glow: "shadow-[0_0_30px_rgba(16,185,129,0.6)] ring-1 ring-emerald-400/40",
    panel: "bg-emerald-700/30 text-emerald-300 border-emerald-600/40"
  },
  yellow: {
    badge: "bg-yellow-600/20 text-yellow-400 border-yellow-500/30",
    glow: "shadow-[0_0_30px_rgba(234,179,8,0.6)] ring-1 ring-yellow-400/40",
    panel: "bg-yellow-700/30 text-yellow-300 border-yellow-600/40"
  },
  red: {
    badge: "bg-red-600/20 text-red-400 border-red-500/30",
    glow: "shadow-[0_0_30px_rgba(239,68,68,0.6)] ring-1 ring-red-400/40",
    panel: "bg-red-700/30 text-red-300 border-red-600/40"
  }
};

// ==========================
// HELPERS
// ==========================
const obtenerCierres = async (symbol, tf) => {
  const url = `https://www.okx.com/api/v5/market/candles?instId=${symbol}&bar=${tf}&limit=${CANDLES}`;
  const resp = await fetch(url);
  const data = await resp.json();
  return data.data.map(c => parseFloat(c[4])).reverse();
};

const sma = (data, n) => data.slice(-n).reduce((a, b) => a + b, 0) / n;

const calcularRSI = (cierres, periodo = 14) => {
  let gains = 0, losses = 0;
  for (let i = 1; i <= periodo; i++) {
    const diff = cierres[i] - cierres[i - 1];
    diff >= 0 ? (gains += diff) : (losses += Math.abs(diff));
  }
  if (losses === 0) return 100;
  const rs = (gains / periodo) / (losses / periodo);
  return 100 - 100 / (1 + rs);
};

// ==========================
// COMPONENT
// ==========================
export default function TerminalFinanciera() {
  const [data, setData] = useState([]);

  const actualizarAsset = async (symbol) => {
    const c4 = await obtenerCierres(symbol, TIMEFRAME);
    const cW = await obtenerCierres(symbol, WEEK_TF);

    const precio = c4.at(-1);
    const ma10 = sma(c4, 10);
    const ma30 = sma(c4, 30);
    const rsi = calcularRSI(c4.slice(-(RSI_PERIOD + 1)));

    const momentum4H = sma(c4, 10) - sma(c4, 21);
    const momentumW = sma(cW, 10) - sma(cW, 21);
    const weeklyOK = momentumW > 0;

    let fase, accion, color;

    if (ma10 > ma30 && momentum4H > 0 && weeklyOK && rsi < 72) {
      fase = "🟢 EXPANSIÓN";
      accion = "SEGUIR CON EL BOT";
      color = "emerald";
    } else if (ma10 > ma30 && momentum4H > 0 && !weeklyOK) {
      fase = "🟡 SOBREEXTENSIÓN";
      accion = "VIGILAR / NO AGREGAR";
      color = "yellow";
    } else {
      fase = "🔴 DISTRIBUCIÓN";
      accion = "CERRAR BOT";
      color = "red";
    }

    return { symbol, precio, rsi, fase, accion, color };
  };

  const loop = async () => {
    const res = [];
    for (const a of ASSETS) res.push(await actualizarAsset(a));
    setData(res);
  };

  useEffect(() => {
    loop();
    const i = setInterval(loop, UPDATE_MS);
    return () => clearInterval(i);
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 pb-24 font-sans">
      <div className="p-10 max-w-5xl mx-auto">

        {/* TITULO PRINCIPAL */}
        <div className="flex justify-center items-center h-40 bg-slate-900 mb-10">
  <h1 className="text-4xl md:text-6xl font-bold uppercase tracking-wider flex gap-2 items-center">
    
    {/* BTC */}
    {["B","T","C"].map((char, idx) => (
      <span
        key={idx}
        className="inline-block text-emerald-400 animate-wave"
        style={{ animationDelay: `${idx * 0.1}s` }}
      >
        {char}
      </span>
    ))}
    <span
      className="inline-block text-emerald-400 animate-wave"
      style={{ animationDelay: `0.4s` }}
    >
      ₿
    </span>

    <span className="mx-2"></span>

    {/* GOLD */}
    {["G","O","L","D"].map((char, idx) => (
      <span
        key={idx}
        className="inline-block text-yellow-400 animate-wave"
        style={{ animationDelay: `${idx * 0.1}s` }}
      >
        {char}
      </span>
    ))}
    <span
      className="inline-block text-yellow-400 animate-wave"
      style={{ animationDelay: `0.5s` }}
    >
      🪙
    </span>
  </h1>

  {/* ANIMACIÓN WAVE */}
  <style jsx>{`
    @keyframes wave {
      0%, 60%, 100% { transform: translateY(0); }
      30% { transform: translateY(-8px); }
    }
    .animate-wave {
      display: inline-block;
      animation: wave 1s infinite;
    }
  `}</style>
</div>


        {/* ASSETS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
          {data.map(a => {
            const c = COLOR_MAP[a.color];

            return (
              <div
                key={a.symbol}
                className={`
                  group relative rounded-2xl border border-slate-700/80
                  bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950
                  p-6 transition-all duration-500
                  hover:-translate-y-1 hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(0,255,255,0.4)] ${c.glow}
                `}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg md:text-xl font-bold tracking-wide">{a.symbol}</h2>
                  <span className={`text-xs md:text-sm font-semibold px-3 py-1 rounded-full border backdrop-blur-sm ${c.badge}`}>
                    {a.fase}
                  </span>
                </div>

                {/* Data */}
                <div className="grid grid-cols-2 gap-y-4 text-sm">
                  <div>
                    <p className="text-slate-400 mb-1 uppercase">Precio</p>
                    <p className="font-mono text-base md:text-lg font-semibold text-white">
                      {a.precio?.toLocaleString()}
                      <span className="text-xs md:text-sm text-slate-400 ml-1">USDT</span>
                    </p>
                  </div>

                  <div>
                    <p className="text-slate-400 mb-1 uppercase">RSI</p>
                    <p className="text-base md:text-lg font-semibold">{a.rsi?.toFixed(1)}</p>
                  </div>
                </div>

                {/* Action Panel */}
                <div className={`mt-5 text-center text-sm md:text-base font-medium rounded-xl py-2 border transition-all duration-300 ${c.panel}`}>
                  {a.accion}
                </div>
              </div>
            );
          })}
        </div>

        {/* GUÍA EN 3 CARTELES */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="rounded-2xl border border-emerald-500/40 bg-slate-800/70 p-5">
            <h3 className="text-emerald-400 font-semibold mb-2 text-lg">🟢 EXPANSIÓN</h3>
            <p className="text-sm md:text-base text-slate-300">
              El activo está en tendencia alcista saludable. Las medias móviles muestran que la presión compradora sigue fuerte, y el RSI indica que todavía no está sobrecomprado.
            </p>
          </div>

          <div className="rounded-2xl border border-yellow-500/40 bg-slate-800/70 p-5">
            <h3 className="text-yellow-400 font-semibold mb-2 text-lg">🟡 SOBREEXTENSIÓN</h3>
            <p className="text-sm md:text-base text-slate-300">
              El precio sigue alcista, pero ya está sobrecomprado, hay riesgo de corrección. El mercado puede empezar a enfriarse.
            </p>
          </div>

          <div className="rounded-2xl border border-red-500/40 bg-slate-800/70 p-5">
            <h3 className="text-red-400 font-semibold mb-2 text-lg">🔴 DISTRIBUCIÓN</h3>
            <p className="text-sm md:text-base text-slate-300">
              El activo está en tendencia bajista o consolidación de venta. Las manos fuertes están deshaciendo.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
